import finishedSubIcon from '@/assets/icons/finished-sub-icon.png';
import inProgressSubIcon from '@/assets/icons/in-progress-sub-icon.png';
import notStartedSubIcon from '@/assets/icons/not-started-sub-icon.png';
import { Project } from '@/stores/ProjectStore';

type ProjectStatus =
  | 'Active'
  | 'Completed'
  | 'PM Preferred'
  | 'BO Approved'
  | 'Pencils Down'
  | 'Archived';
type SecondaryStatus =
  | 'Resiliency'
  | 'Decarbonization'
  | 'Project Details'
  | null;

interface StatusStyle {
  bg: string;
  color: string;
}

/**
 * Determine the main and secondary status for a project based on its actual fields
 */
export function getProjectStatus(project: Project): {
  mainStatus: ProjectStatus;
  secondaryStatus: SecondaryStatus;
} {
  // Determine main status based on project approval fields
  // Check approvals first since they represent progression beyond pencils down
  let mainStatus: ProjectStatus = 'Active';

  if (project.deletedAt) {
    mainStatus = 'Archived';
  } else if (project.budgetApproved) {
    mainStatus = 'BO Approved';
  } else if (project.designApproved) {
    mainStatus = 'PM Preferred';
  } else if (project.pencilsDown) {
    mainStatus = 'Pencils Down';
  }

  // Determine secondary status based on current workflow
  // Map the backend workflow field to frontend display names
  let secondaryStatus: SecondaryStatus = null;

  if (project.currentWorkflow === 'decarb') {
    secondaryStatus = 'Decarbonization';
  } else if (project.currentWorkflow === 'resiliency') {
    secondaryStatus = 'Resiliency';
  } else if (project.currentWorkflow === 'details') {
    secondaryStatus = 'Project Details';
  }

  return { mainStatus, secondaryStatus };
}

/**
 * Get styling for main project status
 */
export function getMainStatusStyle(status: ProjectStatus): StatusStyle {
  switch (status) {
    case 'Active':
      return { bg: 'rgba(9, 126, 113, 1)', color: '#FFFFFF' };
    case 'Completed':
      return { bg: 'rgba(29, 185, 151, 1)', color: '#FFFFFF' };
    case 'PM Preferred':
      return { bg: 'rgba(67, 167, 220, 1)', color: '#FFFFFF' };
    case 'BO Approved':
      return { bg: 'rgba(29, 185, 151, 1)', color: '#FFFFFF' };
    case 'Pencils Down':
      return { bg: 'rgba(123, 188, 177, 1)', color: 'rgba(19, 54, 86, 1)' };
    case 'Archived':
      return { bg: 'rgba(14, 70, 63, 1)', color: '#FFFFFF' };
    default:
      return { bg: 'rgba(9, 126, 113, 1)', color: '#FFFFFF' };
  }
}

/**
 * Get styling for secondary project status
 */
export function getSecondaryStatusStyle(status: string): StatusStyle {
  switch (status) {
    case 'Resiliency':
      return { bg: 'rgba(67, 167, 220, 1)', color: '#FFFFFF' };
    case 'Decarbonization':
      return { bg: 'rgba(57, 171, 114, 1)', color: '#FFFFFF' };
    case 'Project Details':
      return { bg: 'rgba(0, 93, 91, 1)', color: '#FFFFFF' };
    default:
      return { bg: 'rgba(67, 167, 220, 1)', color: '#FFFFFF' };
  }
}

/**
 * Get icon path for subproject status
 */
export function getSubprojectStatusIcon(status: string): string {
  switch (status) {
    case 'not-started':
      return notStartedSubIcon;
    case 'in-progress':
      return inProgressSubIcon;
    case 'finished':
      return finishedSubIcon;
    default:
      return notStartedSubIcon;
  }
}

/**
 * Get display text for time ago based on project index
 */
export function getTimeAgoDisplay(projectIndex: number): string {
  const timeAgoOptions = [
    '2 mins ago',
    '5 mins ago',
    '10 mins ago',
    '1 hour ago',
    '2 hours ago',
    '1 day ago',
  ];
  return timeAgoOptions[projectIndex % timeAgoOptions.length];
}
